import * as React from 'react';
import { MdApartment, MdAttachMoney, MdReceiptLong, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import DataGrid from '../../../Components/Table/DataGrid';
import PageTitle from '../../../Components/Common/PageTitle';
import { useAuth } from '../../../Context/AuthContext';
import { APPRoutes } from '../../../Utils/Route';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../../Utils/Permissions';
import { getOrganizationsRevenue } from '../../../Services/AnalyticsService';
import { formatCurrency, revenueMetricColumns } from './revenueUtils';

const LIST_METRICS = ['visits', 'charges', 'collected', 'adjustment'];

export default function AnalyticsRevenueList() {
  const navigate = useNavigate();
  const { token, permissions, decodedToken } = useAuth();
  const userPermissionCodes = React.useMemo(
    () => resolveUserPermissionCodes({ permissions, decodedToken }),
    [permissions, decodedToken],
  );
  const canViewDetails = permissionHas(userPermissionCodes, PermissionCodes.ANALYTICS_ORGANIZATIONS_VIEW, decodedToken);

  const [rows, setRows] = React.useState([]);
  const [search, setSearch] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  const loadRevenue = React.useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const response = await getOrganizationsRevenue(token);
      if (response?.is_success === false) throw new Error(response?.message || 'Failed to load revenue analytics');
      setRows(response?.data?.organizations || []);
    } catch (err) {
      console.error('Error loading revenue analytics:', err);
      setRows([]);
      toast.error('Unable to load revenue analytics.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  React.useEffect(() => {
    loadRevenue();
  }, [loadRevenue]);

  const filteredRows = React.useMemo(() => {
    const term = search.trim().toLowerCase();
    return term ? rows.filter((row) => (row.organization_name || '').toLowerCase().includes(term)) : rows;
  }, [rows, search]);

  const stats = React.useMemo(() => {
    const sum = (key) => rows.reduce((total, row) => total + (Number(row[key]) || 0), 0);
    return [
      {
        label: 'Organizations',
        value: rows.length.toLocaleString(),
        icon: <MdApartment size={22} color="var(--hc-brand-ink)" />,
        iconBg: 'var(--hc-icon-blue-alt-bg)',
        accentClass: 'border-primary',
        valueClass: 'text-primary',
      },
      {
        label: 'Billed Charges',
        value: formatCurrency(sum('charges')),
        icon: <MdReceiptLong size={22} color="var(--hc-icon-amber-text)" />,
        iconBg: 'var(--hc-icon-amber-bg)',
        accentClass: 'border-warning',
        valueClass: 'text-warning',
      },
      {
        label: 'Collected',
        value: formatCurrency(sum('collected')),
        icon: <MdAttachMoney size={22} color="var(--hc-icon-emerald-text)" />,
        iconBg: 'var(--hc-icon-emerald-bg)',
        accentClass: 'border-success',
        valueClass: 'text-success',
      },
    ];
  }, [rows]);

  const goToOrganization = React.useCallback((row) => {
    navigate(APPRoutes.ANALYTICS_REVENUE_VIEW.replace(':organization_id', row.organization_id), {
      state: { organization_name: row.organization_name },
    });
  }, [navigate]);

  const columns = React.useMemo(() => [
    {
      id: 'organization_name',
      label: 'Organization',
      minWidth: 220,
      format: (_value, row) => (canViewDetails ? (
        <button type="button" className="btn p-0 text-start text-dark border-0 bg-transparent" style={{ font: 'inherit' }} onClick={() => goToOrganization(row)}>
          {row.organization_name || '—'}
        </button>
      ) : (row.organization_name || '—')),
    },
    ...revenueMetricColumns(LIST_METRICS),
    ...(canViewDetails ? [{
      id: 'actions',
      label: 'Action',
      minWidth: 80,
      align: 'center',
      format: (_v, row) => (
        <button
          type="button"
          title="View"
          className="btn btn-sm p-1 lh-1 text-primary border-0 rounded-2"
          style={{ backgroundColor: 'var(--hc-status-info-bg)' }}
          onClick={() => goToOrganization(row)}
        >
          <MdVisibility size={16} />
        </button>
      ),
    }] : []),
  ], [canViewDetails, goToOrganization]);

  return (
    <div>
      <PageTitle title="Revenue - Aegis 360" />
      <div className="mb-4">
        <h4 className="fw-bold mb-1 hc-text-primary fs-3">Revenue Analytics</h4>
        <p className="text-secondary mb-0 small">
          All-time billed charges and collections by organization. Open an organization for collection rates, payers and balances.
        </p>
      </div>

      <div className="row g-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="col-12 col-sm-6 col-xl">
            <div className="position-relative overflow-hidden bg-white p-4 rounded-4 shadow p-3 d-flex align-items-center gap-3 h-100 hc-list-kpi-card">
              <div className={`position-absolute start-0 top-0 h-100 border-start border-5 ${stat.accentClass}`} />
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                style={{ width: 44, height: 44, backgroundColor: stat.iconBg }}
              >
                {stat.icon}
              </div>
              <div className="d-flex flex-column fw-bold lh-sm flex-grow-1 text-break">
                <span className="text-dark" style={{ fontSize: '0.85rem' }}>
                  {stat.label}
                </span>
                {loading ? (
                  <span className="skeleton rounded-2 d-block mt-1" style={{ height: 22, width: '60%' }} />
                ) : (
                  <span className={`fw-bold ${stat.valueClass}`} style={{ fontSize: '1.25rem' }}>
                    {stat.value}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-3 shadow-lg p-3 p-md-4">
        <div className="position-relative mb-3">
          <span className="position-absolute top-50 translate-middle-y d-inline-flex align-items-center justify-content-center text-secondary pe-none" style={{ left: '12px' }}>
            <MdSearch size={18} />
          </span>
          <input
            type="text"
            className="form-control form-control-sm hc-input"
            placeholder="Search by organization..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            style={{ paddingLeft: '36px', height: '40px', width: '100%' }}
          />
        </div>

        <DataGrid
          columns={columns}
          rows={filteredRows}
          loading={loading}
          defaultRowsPerPage={10}
          stickyHeader
          getRowId={(row) => row.organization_id}
        />
      </div>
    </div>
  );
}
